/* A button. Semantic `<button>`, never a div with a click handler.
 *
 * Class names are exactly those in `design/components/button`. The loading
 * state is `aria-busy`, which is the mockup's design and the better one: the
 * announced state and the styled state are the same attribute, so they cannot
 * drift. A busy button is also disabled, because a second submission while the
 * first is in flight is the double-charge bug in every checkout ever written.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useTranslate } from '../i18n/index.js';
import { useWorkspaceLocked } from './workspace-lock.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  /** Full width. The single-column auth screens use this throughout. */
  readonly block?: boolean;
  readonly busy?: boolean;
  /**
   * Keeps this control live while the workspace is read-only — STORY-045.
   *
   * For a submission that REDUCES usage, and for nothing else. The lock exists
   * to stop a workspace growing past a plan it no longer pays for; a control
   * that shrinks it is the way out, and disabling that would make the feature a
   * trap rather than a limit.
   */
  readonly allowedWhileLocked?: boolean;
  readonly children: ReactNode;
}

export function Button({
  variant = 'primary',
  block = false,
  busy = false,
  disabled = false,
  allowedWhileLocked = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, block ? 'btn--block' : null].filter(Boolean).join(' ');
  const t = useTranslate();

  /* Only a SUBMIT is locked.
   *
   * Everything that gets a workspace back under its plan — deleting, moving an
   * Event to Completed, everything on the billing screen — is a click handler
   * on a plain button, so this leaves all of them alone without needing a list
   * of exceptions to maintain. The `type` default below is what makes that
   * distinction trustworthy: submitting is always explicit here. */
  const lockedOut = useWorkspaceLocked() && type === 'submit' && !allowedWhileLocked;

  return (
    <button
      {...rest}
      /* `type` defaults to "submit" inside a form, which fires the form on any
       * button that forgot to say otherwise. Defaulting to "button" makes
       * submitting explicit. */
      type={type}
      className={classes}
      /* **`aria-disabled` for the lock, `disabled` for everything else**, and
       * the difference is the whole reason somebody can find out why.
       *
       * A truly `disabled` button receives no mouse events, so its `title`
       * never appears — the explanation was attached to the one control that
       * could not show it. It is also removed from the tab order, so a keyboard
       * or screen-reader user meets a Save that has silently vanished.
       *
       * `aria-disabled` keeps it focusable and hoverable and still announces
       * "dimmed"/"unavailable", and the handler below is what makes it inert.
       * `busy` and an explicit `disabled` stay genuinely disabled: those are
       * momentary and have nothing to explain. */
      disabled={disabled || busy}
      aria-disabled={lockedOut || undefined}
      /* Says why on the control itself. The banner above carries the numbers
       * and the way out; a dimmed button with no explanation is the thing that
       * made this read as broken in the first place. */
      title={lockedOut ? t('over_limit.control_disabled') : rest.title}
      onClick={(event) => {
        /* Inert, since the element is still clickable and still in a form.
         * Covers the keyboard too: Enter and Space on a focused button both
         * dispatch a click. */
        if (lockedOut) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        rest.onClick?.(event);
      }}
      aria-busy={busy || undefined}
    >
      {children}
    </button>
  );
}
